import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, X, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported, getPushStatus, enablePushNotifications, disablePushNotifications,
} from "@/lib/pushNotifications";

const DISMISS_KEY = "doctobook_push_prompt_dismissed_at";
const DISMISS_DAYS = 7;

/**
 * Bannière d'invitation aux notifications push.
 * Affichée :
 *   - aux pros (doctor / pharmacy) uniquement
 *   - si le navigateur supporte Web Push
 *   - si l'utilisateur n'a pas encore donné de permission
 *   - et n'a pas dismissé récemment (< 7 j)
 */
export default function PushOptInBanner() {
  const { user } = useAuth();
  const isPro = ["doctor", "pharmacy"].includes(user?.role);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isPro) return;
    (async () => {
      const s = await getPushStatus();
      setStatus(s);
    })();
    // Honor previous dismiss
    const at = localStorage.getItem(DISMISS_KEY);
    if (at) {
      const ageDays = (Date.now() - parseInt(at, 10)) / (1000 * 60 * 60 * 24);
      if (ageDays < DISMISS_DAYS) setDismissed(true);
    }
  }, [isPro]);

  if (!isPro || !status?.supported || status.subscribed || status.permission === "denied" || dismissed) {
    return null;
  }

  const enable = async () => {
    setSubmitting(true);
    try {
      const res = await enablePushNotifications();
      if (res.ok) {
        toast.success("Notifications activées · vous serez alerté(e) sans manquer une question dans votre spécialité.");
        const s = await getPushStatus();
        setStatus(s);
      } else if (res.reason === "denied") {
        toast.error("Vous avez refusé l'accès. Vous pouvez l'activer plus tard dans vos paramètres navigateur.");
      } else {
        toast.error("Impossible d'activer les notifications (" + res.reason + ").");
      }
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div className="bg-gradient-to-r from-[#618DB8] to-[#2A4365] text-white rounded-2xl p-5 mb-6 shadow-md relative overflow-hidden" data-testid="push-optin-banner">
      <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full" />
      <div className="absolute -right-12 -bottom-8 w-24 h-24 bg-white/5 rounded-full" />
      <button onClick={dismiss} className="absolute top-3 right-3 text-white/70 hover:text-white z-10" data-testid="push-dismiss-btn" aria-label="Plus tard">
        <X className="h-4 w-4" />
      </button>
      <div className="relative flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
          <Bell className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-display text-lg font-extrabold">Ne manquez plus une question dans votre spécialité</h3>
            <Sparkles className="h-4 w-4 text-amber-300 animate-pulse" />
          </div>
          <p className="text-sm text-white/85 leading-relaxed mb-3">
            Activez les notifications navigateur pour être alerté(e) dès qu&apos;un patient pose une question correspondant à votre spécialité, qu&apos;un confrère vous envoie un message ou qu&apos;un post est validé. <strong className="font-semibold">Aucun contenu médical n&apos;est envoyé dans la notification</strong> (conformité RGPD &amp; HDS).
          </p>
          <div className="flex flex-wrap gap-2 items-center text-xs text-white/80 mb-4">
            <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur rounded-full px-2 py-1">
              <CheckCircle2 className="h-3 w-3" />Anonyme &amp; sans contenu médical
            </span>
            <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur rounded-full px-2 py-1">
              <CheckCircle2 className="h-3 w-3" />Désactivable en 1 clic
            </span>
            <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur rounded-full px-2 py-1">
              <CheckCircle2 className="h-3 w-3" />Web Push natif (pas de Firebase)
            </span>
          </div>
          <div className="flex gap-2">
            <Button onClick={enable} disabled={submitting} className="bg-white text-[#2A4365] hover:bg-white/90 font-bold" data-testid="push-enable-btn">
              <Bell className="h-4 w-4 me-1.5" />{submitting ? "Activation…" : "Activer les notifications"}
            </Button>
            <Button onClick={dismiss} variant="ghost" className="text-white hover:bg-white/10" data-testid="push-later-btn">Plus tard</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Bouton paramètres (à mettre dans /dashboard/profile ou settings) */
export function PushSettingsToggle() {
  const { user } = useAuth();
  const isPro = ["doctor", "pharmacy"].includes(user?.role);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { (async () => setStatus(await getPushStatus()))(); }, []);

  if (!isPro || !isPushSupported()) return null;
  if (!status) return null;

  const toggle = async () => {
    setSubmitting(true);
    try {
      if (status.subscribed) {
        await disablePushNotifications();
        toast.info("Notifications désactivées.");
      } else {
        const res = await enablePushNotifications();
        if (res.ok) toast.success("Notifications activées.");
        else if (res.reason === "denied") toast.error("Permission refusée par votre navigateur.");
      }
      setStatus(await getPushStatus());
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Push test envoyée — devrait apparaître dans 1-2 s");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-3 mb-2">
        {status.subscribed ? <Bell className="h-5 w-5 text-emerald-600" /> : <BellOff className="h-5 w-5 text-slate-400" />}
        <div className="flex-1">
          <div className="font-bold text-sm text-deep">Notifications navigateur</div>
          <div className="text-xs text-slate-500">
            {status.permission === "denied" ? "Bloquées par votre navigateur — autorisez Doctobook dans les paramètres." :
              status.subscribed ? "Activées · vous recevez les alertes santé spécialité, messages, modération." :
              "Désactivées — activez pour ne rien manquer."}
          </div>
        </div>
        <Button onClick={toggle} disabled={submitting || status.permission === "denied"} variant={status.subscribed ? "outline" : "default"} data-testid="push-toggle-btn">
          {status.subscribed ? "Désactiver" : "Activer"}
        </Button>
      </div>
      {status.subscribed && (
        <button onClick={sendTest} className="text-xs text-[#618DB8] hover:underline" data-testid="push-test-btn">
          Envoyer une push de test
        </button>
      )}
    </div>
  );
}
